import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../api/ResourcesApi";
import type { ResourceCreate, ResourceItem, ResourceUpdate } from "../api/ResourcesApi";

interface ResourcesContextValue {
  resources: ResourceItem[];
  loading: boolean;
  error: string;
  includeArchived: boolean;
  setIncludeArchived: (v: boolean) => void;
  reload: () => Promise<void>;

  createResource: (data: ResourceCreate) => Promise<void>;
  updateResource: (id: number, data: ResourceUpdate) => Promise<void>;
  archiveResource: (id: number) => Promise<void>;
  restoreResource: (id: number) => Promise<void>;
  deleteResource: (id: number) => Promise<void>;
}

const ResourcesContext = createContext<ResourcesContextValue | null>(null);

export function ResourcesProvider({ children }: { children: React.ReactNode }) {
  const [resources, setResources] = useState<ResourceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const hasLoadedOnce = useRef(false);

  const reload = useCallback(async () => {
    // Only show the loading state on the very first fetch — reloads triggered by a
    // mutation (enable/disable, archive, etc.) should refresh silently in the
    // background instead of blanking the whole list with a "Loading…" flash.
    if (!hasLoadedOnce.current) setLoading(true);
    try {
      const data = await api.listResources(includeArchived);
      setResources(data);
      setError("");
    } catch {
      setError("Failed to load resources");
    } finally {
      setLoading(false);
      hasLoadedOnce.current = true;
    }
  }, [includeArchived]);

  useEffect(() => { reload(); }, [reload]);

  const createResource = useCallback(async (data: ResourceCreate) => {
    await api.createResource(data);
    await reload();
  }, [reload]);

  const updateResource = useCallback(async (id: number, data: ResourceUpdate) => {
    await api.updateResource(id, data);
    await reload();
  }, [reload]);

  const archiveResource = useCallback(async (id: number) => {
    await api.archiveResource(id);
    await reload();
  }, [reload]);

  const restoreResource = useCallback(async (id: number) => {
    await api.restoreResource(id);
    await reload();
  }, [reload]);

  const deleteResource = useCallback(async (id: number) => {
    await api.deleteResource(id);
    await reload();
  }, [reload]);

  const value = useMemo<ResourcesContextValue>(() => ({
    resources, loading, error, includeArchived, setIncludeArchived, reload,
    createResource, updateResource, archiveResource, restoreResource, deleteResource,
  }), [resources, loading, error, includeArchived, reload, createResource, updateResource, archiveResource, restoreResource, deleteResource]);

  return <ResourcesContext.Provider value={value}>{children}</ResourcesContext.Provider>;
}

export function useResources(): ResourcesContextValue {
  const ctx = useContext(ResourcesContext);
  if (!ctx) throw new Error("useResources must be used within ResourcesProvider");
  return ctx;
}
